import cx from 'classnames';
import { useContext } from 'react';
import GlobalContext from '~/context/vdp/GlobalContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useGetCtaWrapperContent from './hooks/useGetCtaWrapperContent';
import { useScrollDetection } from './hooks/useScrollDetection';

type OwnProps = React.PropsWithChildren<{
  inMobileFooter?: boolean;
  shouldShow?: boolean;
}>;

export default function CtaWrapper({
  inMobileFooter = false,
  children,
  shouldShow = true
}: OwnProps) {
  const { isBreadcrumbScrolledOut } = useContext(GlobalContext);
  const { content, showCtaFooter } = useGetCtaWrapperContent();
  const hasScrolled = useScrollDetection();
  const { purchasePending } = useAlertStatus();

  const shouldHideContent = hasScrolled && inMobileFooter && showCtaFooter;
  const shouldAnimate = inMobileFooter && showCtaFooter;

  const roundableFooter = !inMobileFooter && showCtaFooter;
  const roundBottom = roundableFooter && !isBreadcrumbScrolledOut;

  return shouldShow ? (
    <div
      data-testid="cta-footer"
      className={cx(
        'flex flex-col',
        shouldAnimate && 'transition-all duration-300 ease-in-out',
        shouldHideContent ? 'gap-0' : 'gap-xl',
        { 'bg-background-strong': showCtaFooter },
        inMobileFooter
          ? 'px-2xl pb-2xl pt-lg'
          : [
              'px-2xl pb-2xl lg:px-4xl',
              showCtaFooter ? 'pt-2xl lg:pb-3xl lg:pt-3xl' : 'lg:pb-4xl'
            ],
        { 'sm:rounded-b-xl': roundBottom }
      )}
    >
      {showCtaFooter && content && (
        <div
          className={cx(
            'overflow-hidden',
            shouldAnimate && 'transition-all duration-300 ease-in-out',
            shouldHideContent
              ? 'max-h-0 translate-y-[-8px] transform opacity-0'
              : 'max-h-[300px] translate-y-0 transform opacity-100'
          )}
        >
          <div
            className={cx('flex flex-col gap-md', {
              'md:gap-lg': !purchasePending
            })}
          >
            <p className="text-text-inverse t-heading-md">{content.title}</p>
            <p className="text-text-inverse t-body-sm">{content.description}</p>
          </div>
        </div>
      )}
      {children}
    </div>
  ) : null;
}
